import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import HttpStatus from 'components/Common/HttpStatus'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import config from 'constants/config'
import { cloneElement, createElement, isValidElement, useLayoutEffect, useMemo } from 'react'
import type { ComponentType, ReactElement, ReactNode } from 'react'
import { connect } from 'react-redux'
import {
  createRoutesFromElements,
  matchRoutes,
  Route as RouterRoute,
  Routes as RouterRoutes,
  useLocation,
  useParams,
  useRoutes,
} from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import loadable from 'components/utils/lazyImport'
import {
  OFFER_TYPE_BUNDLE_AND_SAVE_SLUG,
  OFFER_TYPE_HOTEL_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  PRODUCT_TYPE_ULTRALUX_SLUG,
} from 'constants/offer'
import { TYPE_OF_PAGE_HOLIDAY } from 'constants/landingPage'
import { isCoverGeniusEnabled } from 'lib/insurance/insuranceHelpers'
import { isFlightsCredit, isStandaloneFlightsEnabled } from 'selectors/flightsSelectors'
import TripPlannerRoutes from 'tripPlanner/TripPlannerRoutes'
import { LUXURY_ESCAPES } from 'constants/brands'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { LUX_PLUS_MONTH_LANDING_PAGE_URL } from 'takeovers/luxPlusMonthTakeover/constants/luxPlusMonthTakeoverConstants'
import LuxPlusMemberRoute from 'luxPlus/components/Routes/LuxPlusMemberRoute'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import AgentHubGuestPageRoutes from 'agentHub/routes/AgentHubGuestPageRoutes'
import { LE_AGENT_HUB_SOCIETE_CONFIRMATION } from 'agentHub/routes/AgentHubRoutes'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import type { LoadableComponent } from '@loadable/component'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import {
  getOptimizelyFeatureFlagVariation,
  isAllowedToTriggerExperiment,
} from 'lib/optimizely/optimizelyUtils'
import { matchLocaleAwarePath } from 'lib/routing/matchLocaleAwarePath'
import {
  LEGACY_REGIONLESS_REDIRECTS,
  REGIONLESS_DYNAMIC_ROUTE_SUFFIXES,
  type LegacyRegionlessRedirect,
} from 'lib/routing/regionlessPageRoutes'
import type { AppRouteMatch } from './routeTypes'
import { matchDestinationProductDotRoute } from 'lib/offer/matchDestinationProductDotRoute'
import { WEDDING_GIFT_REGISTRY_CART_ROUTE } from 'weddings/constants/giftRegistry'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { LOYALTY_CREDIT_CARD_PAGE, LOYALTY_INFINITE_ONBOARDING_PAGE, LOYALTY_INFINITE_REGISTER_INTEREST_PAGE, LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import getIsLoyaltyPurchasePointsEnabled from 'loyalty/selectors/getIsLoyaltyPurchasePointsEnabled'
import LoyaltyInfiniteMemberRoute from 'loyalty/components/Routes/LoyaltyInfiniteMemberRoute'
import { isMaldivesCharterCarouselEnabled } from 'selectors/maldivesSelectors'
import CorporateEventRoutes from 'corporateEvents/CorporateEventRoutes'
import { getIsMerchandiseEnabled } from 'selectors/merchandiseSelectors'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { getAgentHubAffiliationRules } from 'agentHub/selectors/agentHubSelectors'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { isLEStaff } from 'selectors/accountSelectors'
import { CHARTER_BEFORE_YOU_FLY_PAGE_SLUG, CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG, FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM } from 'constants/friendsAndFamilyDiscount'
import RentalsPage from 'components/Pages/TypesPage/Rentals/RentalsPage'
import { MULTI_CLICK_UNSUB_SUPPORTED_BRANDS } from './constants'
import UrlLocation from 'src/lib/url/UrlLocation'
import { registerPageView } from 'actions/AnalyticsActions'
import AuthRouteComponent from './AuthRouteComponent'
import { isEnglishLocale } from 'i18n/util/locale'

const DestinationLandingPage = loadable(() => import('components/Pages/DestinationLandingPage/DestinationLandingPage'))
const CruiseLocationLandingPage = loadable(() => import('components/Cruises/CruiseSEOpage/CruiseLocationLandingPage'))
const CruiseShipsLandingPage = loadable(() => import('components/Cruises/CruiseSEOpage/CruiseShipsLandingPage'))
const HotelSearchFullScreenPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchFullScreenPage'))
const HotelSearchPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchPage'))
const MetaSearchPage = loadable(() => import('components/Pages/HotelSearchPage/MetaSearchPage'))
const HotelSearchMapPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchMapPage'))
const HotelSearchStoreMapPage = loadable(() => import('components/Pages/HotelSearchPage/HotelSearchStoreMapPage'))
const SupportPage = loadable(() => import('components/Pages/SupportPage/SupportPage'))
const VideoChatPage = loadable(() => import('components/Pages/VideoChatPage'))
const AboutUsPage = loadable(() => import('components/Pages/AboutUsPage'))
const CareersPage = loadable(() => import('components/Pages/CareersPage'))
const StaticPage = loadable(() => import('components/Pages/StaticPage'))
const BestPricePage = loadable(() => import('components/Pages/BestPricePage'))
const FlightsLandingPage = loadable(() => import('components/Pages/PromotionalPages/FlightsLandingPage'))
const FlightsSearchLandingPage = loadable(() => import('components/Pages/PromotionalPages/FlightsLandingPage/FlightsSearchLandingPage'))
const FlightsSearchPage = loadable(() => import('components/Flights/FlightsSearchPage'))
const FlightsSearchResultsPage = loadable(() => import('components/Flights/FlightsSearchResultsPage'))
const FlightsSearchMultiCityResultsPage = loadable(() => import('components/Flights/FlightsSearchMultiCityResultsPage'))
const LegalPages = loadable(() => import('components/Pages/LegalPages'))
const ReferPage = loadable(() => import('marketing/pages/Refer'))
const VouchersPage = loadable(() => import('components/Pages/VouchersPage/VouchersPage'))
const MyEscapesPage = loadable(() => import('components/Pages/MyEscapesPage'))
const ShopOrdersPage = loadable(() => import('components/Pages/ShopOrdersPage'))
const UserReviews = loadable(() => import('components/Pages/UserReviews'))
const OrderPage = loadable(() => import('components/Pages/MyEscapesPage/OrderPage'))
const ShareOrderWebviewPage = loadable(() => import('components/Pages/MyEscapesPage/ShareOrderWebviewPage'))
const ShopOrderPage = loadable(() => import('components/Pages/ShopOrdersPage/OrderPage'))
const UpgradeRoomPage = loadable(() => import('components/Pages/MyEscapesPage/UpgradeRoomPage'))
const AirportTransferPage = loadable(() => import('components/Pages/MyEscapesPage/AirportTransfers'))
const PreferenceCentrePage = loadable(() => import('components/Pages/PreferenceCentrePage'))
const MySubscriptionsPage = loadable(() => import('components/Pages/MySubscriptionsPage'))
const AccountDetailsPage = loadable(() => import('components/Pages/AccountDetailsPage'))
const AccountDetailsTravellerPage = loadable(() => import('components/Pages/AccountDetailsTravellerPage'))
const CreditsAndPaymentPage = loadable(() => import('components/Pages/CreditsPage/CreditsAndPaymentPage'))
const TourTravellerInfoPage = loadable(() => import('components/Pages/TourTravellerInfo/TourTravellerInfoPage'))
const GiftCardConfirmationPage = loadable(() => import('components/Pages/GiftCardsPage/Confirmation'))
const GiftCardViewPage = loadable(() => import('components/Pages/GiftCardsPage/View'))
const LandingPage = loadable(() => import('components/Pages/LandingPage/LandingPage'))
const TravelInsurancePage = loadable(() => import('components/Pages/Insurance/LandingPage'))
const TravelInsuranceCoverGeniusPage = loadable(() => import('components/Pages/Insurance/LandingPage/CoverGenius'))
const TravelInsuranceQuote = loadable(() => import('components/Pages/Insurance/QuotePage'))
const TravelInsuranceAddCheckout = loadable(() => import('components/Pages/Insurance/AddCheckoutPage'))
const WeddingPage = loadable(() => import('weddings/pages/WeddingPage'))
const WeddingSetupPage = loadable(() => import('weddings/pages/WeddingSetupPage'))
const WeddingGiftRegistryCartPage = loadable(() => import('weddings/pages/WeddingGiftRegistryCartPage'))
const WeddingsLandingPage = loadable(() => import('weddings/pages/WeddingsLandingPage'))
const LeLiveLandingPage = loadable(() => import('leLive/pages/LeLiveLandingPage'))
const TravelInsuranceDetails = loadable(() => import('components/Pages/Insurance/DetailsPage'))
const PassportCardDetailsPage = loadable(() => import('components/Pages/Insurance/PassportCardDetailsPage'))
const TravelInsurancePayment = loadable(() => import('components/Pages/Insurance/PaymentPage'))
const EscapesFinderPage = loadable(() => import('components/Pages/EscapesFinderPage'))
const ConfirmationPage = loadable(() => import('checkout/Components/Confirmation/ConfirmationPage'))
const GuestConfirmationPage = loadable(() => import('checkout/Components/Confirmation/GuestConfirmationPage'))
const BNBLLandingPage = loadable(() => import('components/Pages/BNBLLandingPage'))
const TWCLandingPage = loadable(() => import('components/Pages/TWCLandingPage'))
const EWCLandingPage = loadable(() => import('components/Pages/EWCLandingPage'))
const ContentHub = loadable(() => import('components/Pages/LandingPage/Hub'))
const OfferPage = loadable(() => import('components/OfferPage/OfferPage'))
const BedBankOfferPage = loadable(() => import('components/Pages/BedbankOfferPage'))
const PaginatedOffersPage = loadable(() => import('components/Pages/PaginatedOffersPage'))
const TourV2OfferPage = loadable(() => import('components/Pages/TourV2/OfferPage/TourV2OfferPage'))
const TourV2OfferVariationPage = loadable(() => import('components/Pages/TourV2/OfferPage/TourV2OfferVariationPage'))
const TourV2SearchPage = loadable(() => import('components/Pages/TourV2/SearchPage/TourV2SearchPage'))
const HotelsPage = loadable(() => import('components/Pages/TypesPage/Hotels/HotelsPage'))
const HomesAndVillasSearchPage = loadable(() => import('components/Pages/HomeAndVillas/Search/HomesAndVillasSearchPage'))
const HomesAndVillasSearchMapPage = loadable(() => import('components/Pages/HomeAndVillas/Search/HomesAndVillasSearchMapPage'))
const ToursPage = loadable(() => import('components/Pages/TypesPage/ToursPage'))
const UltraLuxVerticalPage = loadable(() => import('components/Pages/TypesPage/UltraLux/UltraLuxVerticalPage'))
const LuxBundlesPage = loadable(() => import('components/Pages/TypesPage/LuxBundlesPage'))
const DestinationProductPage = loadable(() => import('components/Pages/DestinationProductPage/DestinationProductPage'))
const GiftCardsPageContainer = loadable(() => import('components/Pages/GiftCardsPage'))
const PurchaseCreditsPage = loadable(() => import('components/Pages/PurchaseCreditsPage/PurchaseCreditsPage'))
const KlarnaLandingPage = loadable(() => import('components/Pages/PromotionalPages/KlarnaLandingPage'))
const AfterPayLandingPage = loadable(() => import('components/Pages/PromotionalPages/AfterPayLandingPage'))
const HoolahLandingPage = loadable(() => import('components/Pages/PromotionalPages/HoolahLandingPage'))
const RedirectToShopbackPage = loadable(() => import('components/Pages/PromotionalPages/RedirectToShopbackPage'))
const AtomeLandingPage = loadable(() => import('components/Pages/PromotionalPages/AtomeLandingPage'))
const WaaveLandingPage = loadable(() => import('components/Pages/PromotionalPages/WaaveLandingPage'))
const AccountPage = loadable(() => import('components/Pages/AccountPage/AccountLandingPage'))
const ExperienceOfferPage = loadable(() => import('components/Experiences/ExperienceOfferPage/ExperienceOfferPage'))
const ExperiencesPage = loadable(() => import('components/Experiences/ExperiencesPage/ExperiencesPage'))
const TransfersPage = loadable(() => import('components/Transfers/TransfersPage'))
const CarsAirportTransferSearchPage = loadable(() => import('components/Cars/AirportTransfer/CarsAirportTransferSearchPage'))
const ExperienceSearchMapPage = loadable(() => import('components/Experiences/ExperiencesSearch/ExperienceSearchMapPage'))
const CruisesPage = loadable(() => import('components/Cruises/CruisesPage/CruisesPage'))
const CruiseSearchPage = loadable(() => import('components/Cruises/SearchPage/CruiseSearchPage'))
const CruiseOfferPage = loadable(() => import('components/Cruises/CruiseOfferPage/CruiseOfferPage'))
const CruiseLineLandingPage = loadable(() => import('components/Cruises/CruiseLineLandingPage/CruiseLineLandingPage'))
const CruiseDealPage = loadable(() => import('components/Cruises/CruiseDealPage/CruiseDealPage'))
const CruiseDealsListPage = loadable(() => import('components/Cruises/CruiseDealsListPage/CruiseDealsListPage'))
const ExperienceSearchPage = loadable(() => import('components/Experiences/ExperiencesSearch/ExperienceSearchPage'))
const TopPicksForMePage = loadable(() => import('components/Recommendations/TopPicksForMe/TopPicksForMePage'))
const RefereeLandingPage = loadable(() => import('../../../../content/pages/RefereeLanding'))
const GoogleHotelAdPage = loadable(() => import('../../../../marketing/pages/GoogleHotelAdPage'))
const CheckoutPageRoutes = loadable(() => import('checkout/routes'))
const CheckoutRestoreCartPage = loadable(() => import('checkout/Pages/RestoreCartPage'))
const CheckoutGatewayPage = loadable(() => import('checkout/Pages/GatewayPage'))
const RedirectCheckoutBNBL = loadable(() => import('checkout/Pages/RedirectCheckoutBNBL'))
const RedirectCheckoutChangeDates = loadable(() => import('checkout/Pages/RedirectCheckoutChangeDates'))
const RedirectCheckoutConfirmDates = loadable(() => import('checkout/Pages/RedirectCheckoutConfirmDates'))
const RedirectCheckoutExtendDates = loadable(() => import('checkout/Pages/RedirectCheckoutExtendDates'))
const RedirectCheckoutAddExtraGuests = loadable(() => import(/* webpackChunkName: "RedirectCheckoutAddExtraGuests" */ 'checkout/Pages/RedirectCheckoutAddExtraGuests'))
const RedirectCheckoutRoomUpgrade = loadable(() => import('checkout/Pages/RedirectCheckoutRoomUpgrade'))
const RedirectCheckoutFlightChangeDates = loadable(() => import('checkout/Pages/RedirectCheckoutFlightChangeDates'))
const RedirectCheckoutPartnerRoomUpgrade = loadable(() => import('checkout/Pages/RedirectCheckoutPartnerRoomUpgrade'))
const AncillaryPaymentPage = loadable(() => import('checkout/Pages/AncillaryPaymentPage'))
const AncillaryChangeReservationPage = loadable(() => import('checkout/Pages/AncillaryChangeReservationPage'))
const ExperienceVoucher = loadable(() => import('components/Pages/ExperienceVoucher'))
const PropertyPage = loadable(() => import('../../../../marketing/pages/PropertyPage'))
const RedeemGifted = loadable(() => import('components/Pages/RedeemGifted'))
const ExperienceGiftPrintPage = loadable(() => import('components/Pages/ExperienceGiftPrintPage'))
const ExperiencesListPage = loadable(() => import('components/Experiences/ExperiencesList/ExperiencesListPage'))
const AuCompetitionsPage = loadable(() => import('../../../../marketing/pages/AuCompetitionPage'))
const IntlCompetitionsPage = loadable(() => import('../../../../marketing/pages/INTLCompetitionPage'))
const ukCompetitionsPage = loadable(() => import('../../../../marketing/pages/UKCompetitionPage'))
const HolidayTypeLandingPage = loadable(() => import('components/Pages/HolidayTypeLandingPage'))
const CarsPage = loadable(() => import('components/Cars/CarsPage'))
const CarHirePage = loadable(() => import('components/CarHire/CarHirePage/CarHirePage'))
const CarHireSearchPage = loadable(() => import('components/CarHire/CarHireSearchPage/CarHireSearchPage'))
const HomePage = loadable(() => import('../../../../home/pages/HomePage/HomePage'))
const ContentfulWebPage = loadable(() => import('../../../../marketing/pages/ContentfulWebPage/ContentfulWebPage'))
const LuxAwardPage = loadable(() => import('../../../../marketing/pages/ContentfulWebPage/LuxAward/LuxAwardPage'))
const LuxPlusLandingPage = loadable(() => import('luxPlus/pages/LuxPlusLandingPage'))
const LuxPlusAccountPage = loadable(() => import('luxPlus/pages/LuxPlusAccountPage'))
const LoyaltyLandingPage = loadable(() => import('loyalty/pages/LoyaltyLandingPageContainer'))
const LoyaltyCreditCardPage = loadable(() => import('loyalty/pages/loyaltyCreditCardPage/LoyaltyCreditCardPage'))
const InfiniteApplicationPage = loadable(() => import('loyalty/pages/infiniteOnboardingPage/InfiniteApplicationPage'))
const InfiniteOnboardingPage = loadable(() => import('loyalty/pages/infiniteOnboardingPage/InfiniteOnboardingPage'))
const LoyaltyOffersHubPage = loadable(() => import('loyalty/pages/loyaltyOffersHubPage/LoyaltyOffersHubPage'))
const LoungePassesPage = loadable(() => import('loyalty/pages/loungePassesPage/LoungePassesPage'))
const LoyaltyPurchasePointsPage = loadable(() => import('loyalty/pages/loyaltyPurchasePointsPage/LoyaltyPurchasePointsPage'))
const LoyaltyRewardsPage = loadable(() => import('loyalty/pages/loyaltyRewardsPage/LoyaltyRewardsPage'))
const LoyaltySpendPointsPage = loadable(() => import('loyalty/pages/loyaltySpendPointsPage/LoyaltySpendPointsPage'))
const ResetPasswordPage = loadable(() => import('components/Pages/ResetPasswordPage/ResetPasswordPage'))
const FlightDealLandingPage = loadable(() => import('components/Flights/FlightDealLandingPage/FlightDealLandingPage'))
const FlightCampaignsLandingPage = loadable(() => import('components/Flights/FlightCampaignsLandingPage/FlightCampaignsLandingPage'))
const TravelPreferencesPage = loadable(() => import('components/Pages/AccountTravelPreferencesPage/TravelPreferencesPage'))
const AgentHubHomePage = loadable(() => import('agentHub/pages/AgentHubHomepage'))
const AgentHubAccountSetupCompleted = loadable(() => import('agentHub/pages/AgentHubAccountSetup/AgentHubAccountSetupCompleted'))
const AgentHubToursPage = loadable(() => import('agentHub/pages/AgentHubToursPage'))
const SocieteConfirmationPage = loadable(() => import('agentHub/pages/SocieteConfirmationPage'))
const SkyCheckConfirmationPage = loadable(() => import('components/Pages/EmailConfirmedPage/SkyCheckConfirmation/SkyCheckConfirmationPage'))
const TailorMadeTourRequestPage = loadable(() => import('components/Pages/TailorMadeTour/TailorMadeTourRequestPage'))
const TailorMadeTourRequestConfirmationPage = loadable(() => import('components/Pages/TailorMadeTour/TailorMadeTourRequestConfirmationPage'))
const SecurePaymentPage = loadable(() => import('components/Pages/SecurePaymentPage'))
const WhitelabelHomePage = loadable(() => import('whitelabel/pages/WhitelabelHomePage'))
const TravelCompanionsPage = loadable(() => import('components/Pages/TravelCompanionsPage'))
const UserCartPage = loadable(() => import('components/Pages/UserCartPage/SharedCarts/MultiCartReviewWrapper'))
const SharedCartInvitePage = loadable(() => import('components/Pages/UserCartPage/SharedCarts/SharedCartInvitePage'))
const TourCataloguePage = loadable(() => import('components/Pages/TourV2/TourCataloguePage'))
const TourDealPage = loadable(() => import('components/Tours/Deals/TourDealPage'))
const WishlistsIndexPage = loadable(() => import('wishlists/pages/WishlistsIndexPage'))
const WishlistDetailPage = loadable(() => import('wishlists/pages/WishlistDetailPage'))
const MerchandiseOfferPage = loadable(() => import('components/Merchandise/MerchandiseOfferPage/MerchandiseOfferPage'))
const MerchandiseLandingPage = loadable(() => import('components/Merchandise/MerchandiseLandingPage/MerchandiseLandingPage'))
const MerchandiseSearchPage = loadable(() => import('components/Merchandise/MerchandiseSearchPage/MerchandiseSearchPage'))
const MerchandiseBrandsPage = loadable(() => import('components/Merchandise/MerchandiseBrandsPage/MerchandiseBrandsPage'))
const MerchandiseCategoryPage = loadable(() => import('components/Merchandise/MerchandiseCategoryPage/MerchandiseCategoryPage'))
const MerchandiseCategoriesPage = loadable(() => import('components/Merchandise/MerchandiseCategoriesPage'))
const TasteOfGoldDetailsPage = loadable(() => import('takeovers/tasteOfGoldTakeover/pages/TasteOfGoldDetailsPage'))
const LoyaltyEsimDetailsPage = loadable(() => import('loyalty/pages/LoyaltyEsimDetailsPage'))
const LoyaltyEsimInfoPage = loadable(() => import('loyalty/pages/LoyaltyEsimInfoPage'))
const TierProgressionDetailsPage = loadable(() => import('loyalty/pages/TierProgressionDetailsPage'))
const TierUpgradeCampaignPage = loadable(() => import('loyalty/pages/tierUpgradeCampaign/TierUpgradeCampaignPage'))
const TriplePointsTakeoverLandingPage = loadable(() => import('takeovers/societyTriplePointsTakeover/components/TriplePointTakeoverLandingPage'))
const DoublePointsTakeoverLandingPage = loadable(() => import('takeovers/societeDoublePointsTakeover/components/DoublePointTakeoverLandingPage'))
const LuxPlusMonthTakeoverLandingPage = loadable(() => import('takeovers/luxPlusMonthTakeover/components/LuxPlusMonthTakeoverLandingPage'))
const SocieteNavBar = loadable(() => import('loyalty/components/SocieteNavBar'))
const MerchandiseNavBar = loadable(() => import('components/Merchandise/MerchandiseNavBar/MerchandiseNavBar'))
const ResubscribePage = loadable(() => import('components/Pages/ResubscribePage/ResubscribePage'))
const MultiClickUnsubPage = loadable(() => import('components/Pages/MultiClickUnsubPage/MultiClickUnsubPage'))
const MaldivesDirectSearchPage = loadable(() => import('components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectSearchPage'))
const MaldivesDirectBeforeYouFlyPage = loadable(() => import('components/MaldivesDirect/MaldivesDirectBeforeYouFlyPage/MaldivesDirectBeforeYouFlyPage'))
const FriendsAndFamilyInvitePage = loadable(() => import('components/FriendsAndFamilyDiscount/FriendsAndFamilyInvitePage/FriendsAndFamilyInvitePage'))
const AcceptOrderInvitePage = loadable(() => import('components/Pages/AcceptOrderInvitePage/AcceptOrderInvitePage'))

const promotionalPages: Record<string, LoadableComponent<any>> = {
  GiftCardsPageContainer,
  KlarnaLandingPage,
  AfterPayLandingPage,
  HoolahLandingPage,
  RedirectToShopbackPage,
  AtomeLandingPage,
  WaaveLandingPage,
}

interface Props {
  currentRegionCode: string
  standaloneFlightsEnabled: boolean
  standaloneCruiseEnabled: boolean
  isLuxPlusEnabled: boolean
  isLoyaltyEnabled: boolean
}

export interface PageRouteOptions extends Props {
  canBookCarHire: boolean
  canBookCruise: boolean
  hasSetupAgentHubAccount: boolean
  intl: IntlShape
  isFriendsAndFamilyPageEnabled: boolean
  isLoyaltyLuxplusLandingPageEnabled: boolean
  isLoyaltyPurchasePointsEnabled: boolean
  isMaldivesCharterEnabled: boolean
  isShopEnabled: boolean
  isShopLocale: boolean
  isSpoofed: boolean
  isStaff: boolean
  pathname: string
  registerPathnameGatedRoutes?: boolean
}

type BooleanPageRouteOption = Exclude<{
  // -? so optional keys like registerPathnameGatedRoutes do not add undefined
  [K in keyof PageRouteOptions]-?: PageRouteOptions[K] extends boolean ? K : never
}[keyof PageRouteOptions], undefined>

// Turn every boolean route option on. Adding a new boolean to PageRouteOptions
// fails typechecking here so SSR preflight cannot miss a new gate.
const OPTIONAL_ROUTE_FLAGS_ENABLED = {
  canBookCarHire: true,
  canBookCruise: true,
  hasSetupAgentHubAccount: true,
  isFriendsAndFamilyPageEnabled: true,
  isLoyaltyEnabled: true,
  isLoyaltyLuxplusLandingPageEnabled: true,
  isLoyaltyPurchasePointsEnabled: true,
  isLuxPlusEnabled: true,
  isMaldivesCharterEnabled: true,
  isShopEnabled: true,
  isShopLocale: true,
  isSpoofed: true,
  isStaff: true,
  standaloneCruiseEnabled: true,
  standaloneFlightsEnabled: true,
} as const satisfies Record<BooleanPageRouteOption, true>

const OPTIONAL_ROUTE_FLAG_KEYS = Object.keys(
  OPTIONAL_ROUTE_FLAGS_ENABLED,
) as Array<BooleanPageRouteOption>

const DESTINATION_PRODUCT_DOT_ROUTE_PATH =
  '/:regionCode/:destinationProductDotRoute'

const LEGACY_CRUISE_REDIRECTS = [
  { from: '/:regionCode/cruises', to: '/:regionCode/tours' },
  { from: '/:regionCode/search/cruises', to: '/:regionCode/tours' },
  { from: '/:regionCode/cruise-line', to: '/:regionCode/tours' },
] as const

function DefaultHomePageRedirect() {
  const location = useLocation()

  return <RedirectWithStatus
    code={302}
    to={location.pathname.replace(/$/, config.defaultHomePage)}
  />
}

function pageElement(Component: ComponentType<any>) {
  return createElement(Component)
}

function createLegacyRegionlessRedirectRoute(
  redirect: LegacyRegionlessRedirect,
): ReactNode {
  return <RouterRoute
    key={redirect.from}
    path={redirect.from}
    element={<RedirectWithStatus code={redirect.statusCode} to={redirect.to} />}
  />
}

function RentalsRouteElement() {
  const { regionCode } = useParams()

  if (regionCode !== 'au') {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return <RentalsPage />
}

function MultiClickUnsubRouteElement() {
  if (!MULTI_CLICK_UNSUB_SUPPORTED_BRANDS.includes(config.BRAND)) {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return <MultiClickUnsubPage />
}

function ShareThisVerifyRouteElement(): null {
  const mainUrl = `https://platform-api.sharethis.com/js/sharethis.js#property=${config.GOOGLE_APP_ID}`
  const script = document.createElement('script')
  script.setAttribute('id', 'sharethis-js')
  script.src = mainUrl
  script.async = true
  document.body.appendChild(script)
  return null
}

function OfferPageElement() {
  const { offerId } = useParams()

  return <OfferPage key={offerId} />
}

function ExperienceOfferPageElement() {
  const { id, offerId } = useParams()

  return <ExperienceOfferPage key={offerId ?? id} />
}

function BedBankOfferPageElement() {
  const { offerId } = useParams()

  return <BedBankOfferPage key={offerId} />
}

function TourV2OfferPageElement() {
  const { offerId } = useParams()

  return <TourV2OfferPage key={offerId} />
}

function FriendsReferralRedirectElement() {
  const { regionCode, referralEmail } = useParams()
  const clientRedirectTo = useMemo(() => {
    if (!regionCode || !referralEmail) return undefined

    const searchParams = new URLSearchParams({
      [FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM]: referralEmail,
    })

    return `/${regionCode}/campaign/${CHARTER_PAGE_SLUG}?${searchParams.toString()}`
  }, [referralEmail, regionCode])

  useIsomorphicLayoutEffect(() => {
    if (!clientRedirectTo) return

    window.location.replace(clientRedirectTo)
  }, [clientRedirectTo])

  if (typeof window !== 'undefined' && clientRedirectTo) {
    return null
  }

  return (
    <RedirectWithStatus
      code={302}
      to={`/:regionCode/campaign/${CHARTER_PAGE_SLUG}?${FRIENDS_AND_FAMILY_REFERRAL_EMAIL_QUERY_PARAM}=:referralEmail`}
    />
  )
}

function HolidayLandingPageElement() {
  return <LandingPage typeOfPage={TYPE_OF_PAGE_HOLIDAY} />
}

function DestinationProductDotRouteElement() {
  const location = useLocation()
  const match = matchDestinationProductDotRoute(new UrlLocation(location.pathname).withoutLocale().path)

  if (!match) {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return createElement(DestinationProductPage, { match })
}

function FriendsAndFamilyInviteRouteElement(props: { isEnabled: boolean, isStaff: boolean }) {
  if (!props.isStaff || !props.isEnabled) {
    return (
      <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>)
  }

  return <FriendsAndFamilyInvitePage />
}

function StaticPageRouteElement(props: { staticPage: typeof config.staticPages[number], title: string }) {
  const location = useLocation()
  const { staticPage, title } = props

  if (staticPage.type === 'bestPrice') {
    return <BestPricePage />
  } else if (staticPage.type === 'aboutUs') {
    return <AboutUsPage />
  } else if (staticPage.type === 'careersAndCulture') {
    return <CareersPage />
  } else if (staticPage.type === 'termsAndConditions') {
    return <LegalPages slug="terms-and-conditions" location={location} />
  } else if (staticPage.type === 'luxPlusTermsAndConditions') {
    return <LegalPages slug={LUXURY_PLUS_PATHS.TERMS_AND_CONDITIONS_PAGE} location={location} />
  } else if (staticPage.type === 'societeTermsAndConditions') {
    return <LegalPages slug="societe-terms-and-conditions" location={location} />
  } else if (staticPage.type === 'societeFAQs') {
    return <LegalPages slug="societe-frequently-asked-questions" location={location} />
  } else if (staticPage.type === 'shopTermsAndConditions') {
    return <LegalPages slug="shop-terms-and-conditions" location={location} />
  } else if (staticPage.type === 'shopFAQs') {
    return <LegalPages slug="shop-frequently-asked-questions" location={location} />
  } else if (staticPage.type === 'refundPolicy') {
    return <LegalPages slug="refund-policy" location={location} />
  } else if (staticPage.type === 'privacyPolicy') {
    return <LegalPages slug="privacy-policy" location={location} />
  } else if (staticPage.type === 'giftcardTermsAndConditions') {
    return <LegalPages slug="giftcards-terms" location={location} />
  } else if (staticPage.type === 'flights') {
    return <LegalPages slug="flights-policies" location={location} />
  } else if (staticPage.type === 'frequentFlyerPrograms') {
    return <LegalPages slug="frequent-flyer-programs" location={location} />
  } else if (staticPage.type === 'specialProductsPromotions') {
    return <LegalPages slug="promotions" location={location} />
  } else if (staticPage.type === 'bookingPolicies') {
    return <LegalPages slug="booking-policies" location={location} />
  } else if (staticPage.type === 'bestPriceTerms') {
    return <LegalPages slug="best-price-guarantee-terms" location={location} />
  } else if (staticPage.type === 'feeSchedule') {
    return <LegalPages slug="fee-schedule" location={location} />
  } else if (staticPage.type === 'travelInsurance') {
    return <TravelInsurancePage />
  } else if (staticPage.type === 'travelInsuranceCoverGenius') {
    return <TravelInsuranceCoverGeniusPage />
  } else if (staticPage.type === 'bnblLanding') {
    return <BNBLLandingPage />
  } else if (staticPage.type === 'twcLanding') {
    return <TWCLandingPage />
  } else if (staticPage.type === 'ewcLanding') {
    return <EWCLandingPage />
  } else if (staticPage.type === 'affiliatePage') {
    return <ContentfulWebPage />
  }

  return <StaticPage type={staticPage.type} title={title} />
}

interface NativeTrackedRoute {
  element: ReactNode
  index?: boolean
  path?: string
  trackingPath?: string
}

interface LocaleAwareTrackedRoutesProps {
  routes: Array<NativeTrackedRoute>
}

interface AuthenticatedRouteElementProps {
  accountAccessModalInitialMode?: App.UiAccountModalMode
  children?: ReactNode
  loader?: ReactNode
  showPopup?: boolean
  path?: string
}

interface TrackedRouteElementProps {
  element?: ReactNode
  path: string
}

function LocaleAwareTrackedRoutes(props: LocaleAwareTrackedRoutesProps) {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => {
    return {
      ...location,
      pathname: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location])
  const matchedRoute = useMemo(() => {
    const trackingRoutes = props.routes
      .map(route => ({ element: route.element, path: route.trackingPath ?? route.path }))
      .filter((route): route is { element: ReactNode; path: string } => route.path !== undefined)

    return matchRoutes(trackingRoutes, localeStrippedLocation)?.at(-1)
  }, [localeStrippedLocation, props.routes])

  useLayoutEffect(() => {
    if (!matchedRoute) return

    dispatch(registerPageView({
      template: matchedRoute.route.path,
      params: matchedRoute.params,
      path: matchedRoute.pathname,
      search: location.search,
    }))
  }, [dispatch, location.search, matchedRoute])

  return (
    <RouterRoutes location={localeStrippedLocation}>
      {props.routes.map(route => {
        if (route.index) {
          return (
            <RouterRoute
              key={route.trackingPath ?? 'index'}
              index
              element={route.element}
            />
          )
        }

        return (
          <RouterRoute
            key={route.trackingPath ?? route.path}
            path={route.path}
            element={route.element}
          />
        )
      })}
    </RouterRoutes>
  )
}

function TrackedRouteElement(props: TrackedRouteElementProps) {
  const location = useLocation()
  const params = useParams()
  const match = useMemo<AppRouteMatch>(() => ({
    isExact: true,
    params,
    path: props.path,
    url: new UrlLocation(location.pathname).withoutLocale().path,
  }), [location.pathname, params, props.path])

  useRegisterPageView(location, match)

  if (props.element) {
    if (isValidElement(props.element)) {
      return cloneElement(props.element as ReactElement<any>, { location, match })
    }

    return <>{props.element}</>
  }

  return null
}

function AuthenticatedRouteElement(props: AuthenticatedRouteElementProps) {
  const {
    accountAccessModalInitialMode,
    children,
    loader,
    path,
    showPopup,
  } = props
  const location = useLocation()
  const params = useParams()
  const match = useMemo<AppRouteMatch | null>(() => {
    if (!path) return null

    return {
      isExact: true,
      params,
      path,
      url: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location.pathname, params, path])

  return (
    <AuthRouteComponent
      accountAccessModalInitialMode={accountAccessModalInitialMode}
      loader={loader}
      match={match}
      showPopup={showPopup}
    >

      {children}
    </AuthRouteComponent>)
}

function useRegisterPageView(location: ReturnType<typeof useLocation>, match: AppRouteMatch | null) {
  const dispatch = useAppDispatch()

  useLayoutEffect(() => {
    if (!match) return

    dispatch(registerPageView({
      template: match.path,
      params: match.params,
      path: match.url,
      search: location.search,
    }))
  }, [dispatch, location.search, match])
}

function createPageRouteElements(options: PageRouteOptions): ReactNode {
  const {
    canBookCarHire,
    canBookCruise,
    currentRegionCode,
    hasSetupAgentHubAccount,
    intl,
    isFriendsAndFamilyPageEnabled,
    isLoyaltyEnabled,
    isLoyaltyLuxplusLandingPageEnabled,
    isLoyaltyPurchasePointsEnabled,
    isLuxPlusEnabled,
    isMaldivesCharterEnabled,
    isShopEnabled,
    isShopLocale,
    isSpoofed,
    isStaff,
    pathname,
    registerPathnameGatedRoutes = false,
    standaloneCruiseEnabled,
    standaloneFlightsEnabled,
  } = options

  const loyaltyRoutes = (() => {
    // `path` is relative to the `/:regionCode/societe/*` parent route; `trackingPath` is
    // the full template reported to `registerPageView`.
    const routes: Array<NativeTrackedRoute> = [
      { path: 'taste-of-gold/details', trackingPath: '/:regionCode/societe/taste-of-gold/details', element: <TasteOfGoldDetailsPage /> },
      { path: 'esim/details', trackingPath: '/:regionCode/societe/esim/details', element: <LoyaltyEsimDetailsPage /> },
      { path: 'esim/info', trackingPath: '/:regionCode/societe/esim/info', element: <LoyaltyEsimInfoPage /> },
      { path: 'tier-progression/:highestTier', trackingPath: '/:regionCode/societe/tier-progression/:highestTier', element: <TierProgressionDetailsPage /> },
      { path: 'benefits/lounge-passes', trackingPath: '/:regionCode/societe/benefits/lounge-passes', element: <LoungePassesPage /> }]

    if (isLoyaltyPurchasePointsEnabled) {
      routes.push({ path: 'buy-points', trackingPath: '/:regionCode/societe/buy-points', element: <LoyaltyPurchasePointsPage /> })
    }

    if (isMaldivesCharterEnabled) {
      routes.push({ path: 'rewards-seats-maldives', trackingPath: '/:regionCode/societe/rewards-seats-maldives', element: <LoyaltyRewardsPage /> })
    }

    routes.push(
      { path: 'using-my-points', trackingPath: '/:regionCode/societe/using-my-points', element: <LoyaltySpendPointsPage /> },
      { path: 'offers', trackingPath: '/:regionCode/societe/offers', element: <LoyaltyOffersHubPage /> },
      { path: ':campaignSlug', trackingPath: '/:regionCode/societe/:campaignSlug', element: <TierUpgradeCampaignPage /> },
      { index: true, trackingPath: '/:regionCode/societe', element: <LoyaltyLandingPage /> },
    )

    return routes
  })()
  const merchandiseShopNavRoutes: Array<NativeTrackedRoute> = [
    { index: true, trackingPath: '/:regionCode/shop', element: <MerchandiseNavBar /> },
    { path: 'category/:categorySlug', trackingPath: '/:regionCode/shop/category/:categorySlug', element: <MerchandiseNavBar /> }]
  const merchandiseShopPageRoutes: Array<NativeTrackedRoute> = [
    { index: true, trackingPath: '/:regionCode/shop', element: <MerchandiseLandingPage /> },
    { path: 'offer/:offerId', trackingPath: '/:regionCode/shop/offer/:offerId', element: <MerchandiseOfferPage /> },
    { path: 'offer/:slug/:offerId', trackingPath: '/:regionCode/shop/offer/:slug/:offerId', element: <MerchandiseOfferPage /> },
    { path: 'brands', trackingPath: '/:regionCode/shop/brands', element: <MerchandiseBrandsPage /> },
    { path: 'categories', trackingPath: '/:regionCode/shop/categories', element: <MerchandiseCategoriesPage /> },
    { path: 'category/:categorySlug', trackingPath: '/:regionCode/shop/category/:categorySlug', element: <MerchandiseCategoryPage /> }]
  const merchandiseSearchNavRoutes: Array<NativeTrackedRoute> = [
    { path: 'brands/:brandSlug/:brandId', trackingPath: '/:regionCode/search/shop/brands/:brandSlug/:brandId', element: <MerchandiseNavBar /> },
    { path: ':parentCategorySlug/:categorySlug', trackingPath: '/:regionCode/search/shop/:parentCategorySlug/:categorySlug', element: <MerchandiseNavBar /> },
    { path: ':categorySlug?', trackingPath: '/:regionCode/search/shop/:categorySlug?', element: <MerchandiseNavBar /> }]
  const merchandiseSearchPageRoutes: Array<NativeTrackedRoute> = [
    { path: 'brands/:brandSlug/:brandId', trackingPath: '/:regionCode/search/shop/brands/:brandSlug/:brandId', element: <MerchandiseSearchPage /> },
    { path: ':parentCategorySlug/:categorySlug', trackingPath: '/:regionCode/search/shop/:parentCategorySlug/:categorySlug', element: <MerchandiseSearchPage /> },
    { path: ':categorySlug?', trackingPath: '/:regionCode/search/shop/:categorySlug?', element: <MerchandiseSearchPage /> }]
  const friendsReferralRoutes: Array<NativeTrackedRoute> = [
    { path: ':referralEmail', trackingPath: '/:regionCode/friends/:referralEmail', element: <FriendsReferralRedirectElement /> }]
  const isLoyaltyRoute = registerPathnameGatedRoutes ||
    !!matchLocaleAwarePath(pathname, `/:regionCode/${LOYALTY_LANDING_PAGE}`)
  const isTripPlannerRoute = registerPathnameGatedRoutes ||
    !!matchLocaleAwarePath(pathname, '/:regionCode/trip-planner')
  const isMerchandiseRoute = registerPathnameGatedRoutes ||
    !!matchLocaleAwarePath(pathname, [
      '/:regionCode/shop',
      '/:regionCode/search/shop'],
    )
  const corporateEventRouteMatch = matchLocaleAwarePath<{ eventId: string; regionCode: string; }>(pathname, '/:regionCode/corporate-events/:eventId')

  return (
    <>
      {/* Tour v1 redirects */}
      {Object.keys(config.toursV1Redirects).map((tourV1) => <RouterRoute

        key={tourV1}
        path={`/:regionCode/offer/${tourV1}/*`}
        element={<RedirectWithStatus
          key={tourV1}
          code={301}
          to={`/tour/${config.toursV1Redirects[tourV1]}`}
          withSearchAndHash={false}
        />}
      />,

      )}
      {/* Tour v2 redirects */}
      {Object.keys(config.toursV2Redirects).flatMap((tourV2) => [<RouterRoute

        key={`${tourV2}-variation`}
        path={`/:regionCode/tour/${tourV2}/:variationId/*`}
        element={<RedirectWithStatus
          key={`${tourV2}-variation`}
          code={301}
          to={`/tour/${config.toursV2Redirects[tourV2]}`}
        />}
      />, <RouterRoute

        key={tourV2}
        path={`/:regionCode/tour/${tourV2}/*`}
        element={<RedirectWithStatus
          key={tourV2}
          code={301}
          to={`/tour/${config.toursV2Redirects[tourV2]}`}
        />}
      />],

      )}
      {/* Offer list Pages */}
      {config.agentHub.isEnabled && <RouterRoute path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} element={<TrackedRouteElement element={<AgentHubToursPage />} path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} />} />}
      <RouterRoute path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} element={<TrackedRouteElement element={<ToursPage />} path={`/:regionCode/${OFFER_TYPE_TOUR_SLUG}`} />} />
      <RouterRoute path={`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}`} element={<TrackedRouteElement element={<HotelsPage />} path={`/:regionCode/${OFFER_TYPE_HOTEL_SLUG}`} />} />
      <RouterRoute
        path="/:regionCode/rentals"
        element={<TrackedRouteElement
          element={

            <RentalsRouteElement />
          }
          path="/:regionCode/rentals"
        />}
      />

      <RouterRoute path={`/:regionCode/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`} element={<TrackedRouteElement element={<LuxBundlesPage />} path={`/:regionCode/${OFFER_TYPE_BUNDLE_AND_SAVE_SLUG}`} />} />
      {config.ULTRALUX_ENABLED && <RouterRoute path={`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}`} element={<TrackedRouteElement element={<UltraLuxVerticalPage />} path={`/:regionCode/${PRODUCT_TYPE_ULTRALUX_SLUG}`} />} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path="/:regionCode/experiences" element={<TrackedRouteElement element={<ExperiencesPage />} path="/:regionCode/experiences" />} />}
      {config.EXPERIENCE_TRANSFERS_VERTICAL_ENABLED && <RouterRoute path="/:regionCode/transfers" element={<TrackedRouteElement element={<TransfersPage />} path="/:regionCode/transfers" />} />}
      {/* Cruises */}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path="/:regionCode/cruises" element={<TrackedRouteElement element={<CruisesPage />} path="/:regionCode/cruises" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path="/:regionCode/cruises/deals" element={<TrackedRouteElement element={<CruiseDealsListPage />} path="/:regionCode/cruises/deals" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={'/:regionCode/cruises/departures/:departureLocation/*'} element={<TrackedRouteElement element={<CruiseLocationLandingPage />} path="/:regionCode/cruises/departures/:departureLocation" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={'/:regionCode/cruises/destinations/:destinationLocation/*'} element={<TrackedRouteElement element={<CruiseLocationLandingPage />} path="/:regionCode/cruises/destinations/:destinationLocation" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path={'/:regionCode/cruises/ships/:shipNames/*'} element={<TrackedRouteElement element={<CruiseShipsLandingPage />} path="/:regionCode/cruises/ships/:shipNames" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path="/:regionCode/cruises/deal/:slug" element={<TrackedRouteElement element={<CruiseDealPage />} path="/:regionCode/cruises/deal/:slug" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path="/:regionCode/cruises/:offerId" element={<TrackedRouteElement element={<CruiseOfferPage />} path="/:regionCode/cruises/:offerId" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path="/:regionCode/cruises/:offerSlug/:offerId" element={<TrackedRouteElement element={<CruiseOfferPage />} path="/:regionCode/cruises/:offerSlug/:offerId" />} />}
      {standaloneCruiseEnabled && canBookCruise && <RouterRoute path="/:regionCode/cruise-line/:cruiseLineSlug" element={<TrackedRouteElement element={<CruiseLineLandingPage />} path="/:regionCode/cruise-line/:cruiseLineSlug" />} />}
      {!standaloneCruiseEnabled && canBookCruise && LEGACY_CRUISE_REDIRECTS.map(({ from, to }) => <RouterRoute
        key={from}
        path={`${from}/*`}
        element={<RedirectWithStatus code={302} to={to} />}
      />)}
      {/* Car Hire Pages */}
      {config.CARS_AIRPORT_TRANSFER_ENABLED && <RouterRoute path="/:regionCode/cars" element={<TrackedRouteElement element={<CarsPage />} path="/:regionCode/cars" />} />}
      {config.CARS_AIRPORT_TRANSFER_ENABLED && <RouterRoute path="/:regionCode/car-hire" element={<RedirectWithStatus code={301} to="/:regionCode/cars" />} />}
      {!config.CARS_AIRPORT_TRANSFER_ENABLED && config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && canBookCarHire && <RouterRoute path="/:regionCode/car-hire" element={<TrackedRouteElement element={<CarHirePage />} path="/:regionCode/car-hire" />} />}
      {/* Offer pages */}
      <RouterRoute path="/:regionCode/offer/:offerId" element={<TrackedRouteElement element={<OfferPageElement />} path="/:regionCode/offer/:offerId" />} />
      <RouterRoute path={'/:regionCode/offer/:offerSlug/:offerId/*'} element={<TrackedRouteElement element={<OfferPageElement />} path="/:regionCode/offer/:offerSlug/:offerId" />} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path="/:regionCode/experience/:id" element={<TrackedRouteElement element={<ExperienceOfferPageElement />} path="/:regionCode/experience/:id" />} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path={'/:regionCode/experience/:slug/:id/*'} element={<TrackedRouteElement element={<ExperienceOfferPageElement />} path="/:regionCode/experience/:slug/:id" />} />}
      <RouterRoute path="/:regionCode/experiences/:locationSlug/:categorySlug" element={<TrackedRouteElement element={<ExperiencesListPage />} path="/:regionCode/experiences/:locationSlug/:categorySlug" />} />
      <RouterRoute path="/:regionCode/experiences/:locationSlug" element={<TrackedRouteElement element={<ExperiencesListPage />} path="/:regionCode/experiences/:locationSlug" />} />
      <RouterRoute path={'/:regionCode/partner/:offerSlug/:offerId/*'} element={<TrackedRouteElement element={<BedBankOfferPageElement />} path="/:regionCode/partner/:offerSlug/:offerId" />} />
      <RouterRoute path={'/:regionCode/page/:pageNum/*'} element={<TrackedRouteElement element={<PaginatedOffersPage />} path="/:regionCode/page/:pageNum" />} />
      {/* If brand defines a particular default homepage, redirect "/" to that location */}
      {
        config.defaultHomePage && <RouterRoute
          path="/:regionCode/"
          element={<TrackedRouteElement
            element={

              <DefaultHomePageRedirect />
            }
            path="/:regionCode/"
          />}
        />

      }
      {config.agentHub.isEnabled && <RouterRoute path="/:regionCode/" element={<TrackedRouteElement element={<AgentHubHomePage />} path="/:regionCode/" />} />}
      {config.WHITELABEL_HOME_PAGE_ENABLED && <RouterRoute path="/:regionCode/" element={<TrackedRouteElement element={<WhitelabelHomePage />} path="/:regionCode/" />} />}
      <RouterRoute path="/:regionCode/" element={<TrackedRouteElement element={<HomePage />} path="/:regionCode/" />} />
      <RouterRoute path={'/:regionCode/villas/*'} element={<RedirectWithStatus code={301} to="/:regionCode/holidays/homes-and-villas" />} />
      <RouterRoute path={'/:regionCode/doublesix/*'} element={<RedirectWithStatus code={301} to="/:regionCode/offer/double-six-luxury-hotel-seminyak-bali-indonesia-asia/006RF00000KMiBlYAL" />} />
      {/* Support Pages */}
      {config.SUPPORT_ENABLED && <RouterRoute path={'/:regionCode/support/*'} element={<TrackedRouteElement element={<SupportPage />} path="/:regionCode/support" />} />}
      {/* Corporate Events */}
      {config.CORPORATE_EVENTS_ENABLED && (registerPathnameGatedRoutes || corporateEventRouteMatch) && <RouterRoute
        path="/:regionCode/corporate-events/:eventId/*"
        element={
          <CorporateEventRoutes
            eventId={corporateEventRouteMatch?.params.eventId ?? ''}
            regionCode={corporateEventRouteMatch?.params.regionCode ?? ''}
          />
        }
      />}
      {/* Weddings */}
      {config.WEDDINGS_ENABLED && <RouterRoute path="/:regionCode/weddings/create" element={<AuthenticatedRouteElement showPopup path="/:regionCode/weddings/create"><WeddingSetupPage /></AuthenticatedRouteElement>} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path="/:regionCode/weddings/:slug/edit" element={<AuthenticatedRouteElement showPopup path="/:regionCode/weddings/:slug/edit"><WeddingSetupPage /></AuthenticatedRouteElement>} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path={WEDDING_GIFT_REGISTRY_CART_ROUTE} element={<AuthenticatedRouteElement showPopup path={WEDDING_GIFT_REGISTRY_CART_ROUTE}><WeddingGiftRegistryCartPage /></AuthenticatedRouteElement>} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path="/:regionCode/weddings/:slug" element={<TrackedRouteElement element={<WeddingPage />} path="/:regionCode/weddings/:slug" />} />}
      {config.WEDDINGS_ENABLED && <RouterRoute path="/:regionCode/weddings" element={<TrackedRouteElement element={<WeddingsLandingPage />} path="/:regionCode/weddings" />} />}
      {/* LE Live */}
      {config.LE_LIVE_ENABLED && <RouterRoute path="/:regionCode/live" element={<TrackedRouteElement element={<LeLiveLandingPage />} path="/:regionCode/live" />} />}
      {/* Sales Team Contact Page */}
      <RouterRoute path={'/:regionCode/request-video-chat/:offerId/:packageId/*'} element={<TrackedRouteElement element={pageElement(VideoChatPage)} path="/:regionCode/request-video-chat/:offerId/:packageId" />} />
      {/* LuxPlus */}
      {isLuxPlusEnabled && <RouterRoute
        path={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}/:tab?/*`}
        element={<TrackedRouteElement element={<LuxPlusLandingPage />} path={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}/:tab?`} />}
      />}
      {isLuxPlusEnabled && <RouterRoute
        path={'/:regionCode/luxplus/*'}
        element={
          <RedirectWithStatus code={301} to={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} />
        }
      />}

      {/* Loyalty */}
      {isLoyaltyEnabled && <RouterRoute
        path={`/:regionCode/${LOYALTY_INFINITE_ONBOARDING_PAGE}/*`}
        element={<LoyaltyInfiniteMemberRoute showPopup path={`/:regionCode/${LOYALTY_INFINITE_ONBOARDING_PAGE}`} element={<InfiniteOnboardingPage />} />}
      />}
      {isLoyaltyEnabled && <RouterRoute
        path={`/:regionCode/${LOYALTY_CREDIT_CARD_PAGE}`}
        element={<TrackedRouteElement element={<LoyaltyCreditCardPage />} path={`/:regionCode/${LOYALTY_CREDIT_CARD_PAGE}`} />}
      />}
      {isLoyaltyEnabled && <RouterRoute
        path={`/:regionCode/${LOYALTY_INFINITE_REGISTER_INTEREST_PAGE}/*`}
        element={<TrackedRouteElement element={<InfiniteApplicationPage />} path={`/:regionCode/${LOYALTY_INFINITE_REGISTER_INTEREST_PAGE}`} />}
      />}
      {isLoyaltyEnabled && isLoyaltyRoute &&
        <RouterRoute
          path={`/:regionCode/${LOYALTY_LANDING_PAGE}/*`}
          element={
            <>
              <SocieteNavBar />
              <LocaleAwareTrackedRoutes routes={loyaltyRoutes} />
            </>
          }
        />}
      {isLoyaltyEnabled && <RouterRoute
        path={'/:regionCode/society/*'}
        element={
          <RedirectWithStatus code={301} to={`/:regionCode/${LOYALTY_LANDING_PAGE}`} />
        }
      />}
      {isLoyaltyEnabled && <RouterRoute
        path={'/:regionCode/société/*'}
        element={
          <RedirectWithStatus code={301} to={`/:regionCode/${LOYALTY_LANDING_PAGE}`} />
        }
      />}
      {isLoyaltyEnabled && <RouterRoute path={'/:regionCode/triple-points/*'} element={<TrackedRouteElement element={<TriplePointsTakeoverLandingPage />} path="/:regionCode/triple-points" />} />}
      {isLoyaltyEnabled && <RouterRoute path={'/:regionCode/double-status-credits/*'} element={<TrackedRouteElement element={<DoublePointsTakeoverLandingPage />} path="/:regionCode/double-status-credits" />} />}
      {isLuxPlusEnabled && <RouterRoute path={`/:regionCode${LUX_PLUS_MONTH_LANDING_PAGE_URL}/*`} element={<TrackedRouteElement element={<LuxPlusMonthTakeoverLandingPage />} path={`/:regionCode${LUX_PLUS_MONTH_LANDING_PAGE_URL}`} />} />}
      {/* Search Pages */}
      <RouterRoute path={'/:regionCode/search-fullscreen/*'} element={<TrackedRouteElement element={<HotelSearchFullScreenPage />} path="/:regionCode/search-fullscreen" />} />
      <RouterRoute path="/:regionCode/search" element={<TrackedRouteElement element={<HotelSearchPage />} path="/:regionCode/search" />} />
      <RouterRoute path="/:regionCode/metasearch/:propertyId" element={<TrackedRouteElement element={<MetaSearchPage />} path="/:regionCode/metasearch/:propertyId" />} />
      <RouterRoute path="/:regionCode/search/map" element={<TrackedRouteElement element={pageElement(HotelSearchMapPage)} path="/:regionCode/search/map" />} />
      <RouterRoute path="/:regionCode/search/store/map" element={<TrackedRouteElement element={pageElement(HotelSearchStoreMapPage)} path="/:regionCode/search/store/map" />} />
      {config.CARS_AIRPORT_TRANSFER_ENABLED && <RouterRoute path="/:regionCode/search/transfer" element={<TrackedRouteElement element={<CarsAirportTransferSearchPage />} path="/:regionCode/search/transfer" />} />}
      <RouterRoute path="/:regionCode/search/homes-and-villas" element={<TrackedRouteElement element={<HomesAndVillasSearchPage />} path="/:regionCode/search/homes-and-villas" />} />
      <RouterRoute path="/:regionCode/search/homes-and-villas/map" element={<TrackedRouteElement element={pageElement(HomesAndVillasSearchMapPage)} path="/:regionCode/search/homes-and-villas/map" />} />
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path="/:regionCode/search/experiences" element={<TrackedRouteElement element={<ExperienceSearchPage />} path="/:regionCode/search/experiences" />} />}
      {config.EXPERIENCES_STANDLONE_ENABLED && <RouterRoute path="/:regionCode/search/experiences/map" element={<TrackedRouteElement element={<ExperienceSearchMapPage />} path="/:regionCode/search/experiences/map" />} />}
      {config.CRUISES_STANDLONE_ENABLED && canBookCruise && <RouterRoute path="/:regionCode/search/cruises" element={<TrackedRouteElement element={<CruiseSearchPage />} path="/:regionCode/search/cruises" />} />}
      {config.CARHIRE_ENABLED && config.CARHIRE_REGIONS.split(',').includes(currentRegionCode) && canBookCarHire && <RouterRoute path="/:regionCode/search/car-hire" element={<TrackedRouteElement element={<CarHireSearchPage />} path="/:regionCode/search/car-hire" />} />}
      {LEGACY_REGIONLESS_REDIRECTS.map(createLegacyRegionlessRedirectRoute)}
      <RouterRoute path={'/:regionCode/velocity-frequent-flyer/*'} element={<RedirectWithStatus code={301} to="/:regionCode/" />} />
      <RouterRoute path={'/:regionCode/chadstone.p1/*'} element={<RedirectWithStatus code={301} to="/:regionCode/campaign/store" />} />
      <RouterRoute path={'/:regionCode/amex/*'} element={<RedirectWithStatus code={301} to="/:regionCode/" />} />
      <RouterRoute path={'/:regionCode/refund-policy/*'} element={<RedirectWithStatus code={301} to="/:regionCode/booking-policies" />} />
      <RouterRoute path={'/:regionCode/frequently-asked-questions/*'} element={<RedirectWithStatus code={301} to="/:regionCode/support" />} />
      {/* Account pages */}
      <RouterRoute path={'/:regionCode/account/my-destinations/*'} element={<RedirectWithStatus code={301} to="/:regionCode/account" />} />
      <RouterRoute path="/:regionCode/account/my-escapes/upgrade-room/:orderId" element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/upgrade-room/:orderId"><UpgradeRoomPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/account" element={isAgentHubEnabled ? <RedirectWithStatus code={302} to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account"><AccountPage /></AuthenticatedRouteElement>} />
      {config.ENABLE_TRIP_PLANNER && <RouterRoute path="/:regionCode/wishlists" element={<AuthenticatedRouteElement showPopup path="/:regionCode/wishlists"><WishlistsIndexPage /></AuthenticatedRouteElement>} />}
      {config.ENABLE_TRIP_PLANNER && <RouterRoute path="/:regionCode/wishlists/:wishlistId" element={<AuthenticatedRouteElement showPopup path="/:regionCode/wishlists/:wishlistId"><WishlistDetailPage /></AuthenticatedRouteElement>} />}
      <RouterRoute path="/:regionCode/account/credits-payments" element={isAgentHubEnabled ? <RedirectWithStatus code={302} to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account/credits-payments"><CreditsAndPaymentPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/account/vouchers" element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/vouchers"><VouchersPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/experiences/:experienceItemId/voucher"><ExperienceVoucher /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/account/my-escapes/order/:orderId" element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/order/:orderId"><OrderPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/booking-authority/share" element={<AuthenticatedRouteElement showPopup path="/:regionCode/booking-authority/share"><ShareOrderWebviewPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/order/:orderId/experience-gift-details" element={<AuthenticatedRouteElement showPopup path="/:regionCode/order/:orderId/experience-gift-details"><ExperienceGiftPrintPage /></AuthenticatedRouteElement>} />
      {isShopEnabled && <RouterRoute path="/:regionCode/account/shop-orders/order/:orderId" element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/shop-orders/order/:orderId"><ShopOrderPage /></AuthenticatedRouteElement>} />}
      <RouterRoute path="/:regionCode/account/travel-preferences" element={<AuthenticatedRouteElement path="/:regionCode/account/travel-preferences"><TravelPreferencesPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/account/travel-companions" element={isAgentHubEnabled ? <RedirectWithStatus code={302} to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account/travel-companions"><TravelCompanionsPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/account/my-escapes/order/:orderId/airport-transfers/:experienceItemId" element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/order/:orderId/airport-transfers/:experienceItemId"><AirportTransferPage /></AuthenticatedRouteElement>} />
      {/* Apps are still pointing to /my-escapes, so redirect to the updated route */}
      <RouterRoute path={'/:regionCode/my-escapes/:modalType?/*'} element={<RedirectWithStatus code={301} to="/:regionCode/account/my-escapes/:modalType?" />} />
      <RouterRoute path={'/:regionCode/account/my-escapes/:tab?/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/my-escapes/:tab?"><MyEscapesPage /></AuthenticatedRouteElement>} />
      {isShopEnabled && <RouterRoute path={'/:regionCode/account/shop-orders/:tab?/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/account/shop-orders/:tab?"><ShopOrdersPage /></AuthenticatedRouteElement>} />}
      <RouterRoute path="/:regionCode/account/details/traveller" element={<AuthenticatedRouteElement path="/:regionCode/account/details/traveller">{pageElement(AccountDetailsTravellerPage)}</AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/account/details" element={isAgentHubEnabled ? <RedirectWithStatus code={302} to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement showPopup path="/:regionCode/account/details"><AccountDetailsPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/booking-form/:orderId?/*'} element={<AuthenticatedRouteElement path="/:regionCode/booking-form/:orderId?"><TourTravellerInfoPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/review/:orderId?" element={<AuthenticatedRouteElement showPopup path="/:regionCode/review/:orderId?"><UserReviews /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/reset-password/:resetPasswordToken" element={<TrackedRouteElement element={<ResetPasswordPage />} path="/:regionCode/reset-password/:resetPasswordToken" />} />
      {isLuxPlusEnabled && !isLoyaltyLuxplusLandingPageEnabled && <RouterRoute path={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} element={<LuxPlusMemberRoute showPopup end path={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}`} element={<LuxPlusAccountPage />} />} />}
      {isLuxPlusEnabled && isLoyaltyLuxplusLandingPageEnabled && <RouterRoute path={`/:regionCode/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}/*`} element={<RedirectWithStatus code={301} to={`/:regionCode/${LUXURY_PLUS_PATHS.DEFAULT_LANDING_PAGE}`} />} />}

      {/* hidden preference centre page */}
      {config.BRAND === LUXURY_ESCAPES && <RouterRoute
        path={'/:regionCode/account/hidden-communication-preferences/*'}
        element={<AuthenticatedRouteElement
          showPopup
          path="/:regionCode/account/hidden-communication-preferences"
        >
          <MySubscriptionsPage />
        </AuthenticatedRouteElement>}
      />}

      {/* Subscriptions Pages */}
      <RouterRoute path="/:regionCode/resubscribe" element={<TrackedRouteElement element={<ResubscribePage />} path="/:regionCode/resubscribe" />} />
      <RouterRoute
        path="/:regionCode/account/communication-preferences/unsubscribe"
        element={<TrackedRouteElement
          element={

            <MultiClickUnsubRouteElement />
          }
          path="/:regionCode/account/communication-preferences/unsubscribe"
        />}
      />

      <RouterRoute path={'/:regionCode/account/communication-preferences/*'} element={isAgentHubEnabled ? <RedirectWithStatus code={302} to="/:regionCode/account/my-escapes" /> : <AuthenticatedRouteElement path="/:regionCode/account/communication-preferences"><PreferenceCentrePage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/my-subscriptions/*'} element={<RedirectWithStatus code={301} to="/:regionCode/account/communication-preferences" />} />
      <RouterRoute path={'/:regionCode/account/my-subscriptions/*'} element={<RedirectWithStatus code={301} to="/:regionCode/account/communication-preferences" />} />
      <RouterRoute path={'/:regionCode/account/subscriptions/*'} element={<RedirectWithStatus code={301} to="/:regionCode/account/communication-preferences" />} />
      <RouterRoute path="/:regionCode/account/payment-cards" element={<RedirectWithStatus code={301} to="/:regionCode/account/credits-payments" />} />
      <RouterRoute path={'/:regionCode/account-details/*'} element={<RedirectWithStatus code={301} to="/:regionCode/account/details" />} />
      {/* Insurance pages */}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={'/:regionCode/travel-protection/quote/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/quote"><TravelInsuranceQuote /></AuthenticatedRouteElement>} />}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={'/:regionCode/travel-protection/add/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/add"><TravelInsuranceAddCheckout /></AuthenticatedRouteElement>} />}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={'/:regionCode/travel-protection/details/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/details"><TravelInsuranceDetails /></AuthenticatedRouteElement>} />}
      {isCoverGeniusEnabled(currentRegionCode) && <RouterRoute path={'/:regionCode/travel-protection/payment/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/travel-protection/payment">{pageElement(TravelInsurancePayment)}</AuthenticatedRouteElement>} />}
      <RouterRoute path={'/:regionCode/passport-card-insurance/details/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/passport-card-insurance/details">{pageElement(PassportCardDetailsPage)}</AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/insurance/checkout/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection/quote" />} />
      <RouterRoute path={'/:regionCode/insurance/confirmation/*'} element={<RedirectWithStatus code={301} to="/:regionCode/booking/confirmation" />} />
      <RouterRoute path={'/:regionCode/insurance/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection" />} />
      <RouterRoute path={'/:regionCode/insurance/xcover/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection/xcover" />} />
      <RouterRoute path={'/:regionCode/insurance/quote/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection/quote" />} />
      <RouterRoute path={'/:regionCode/insurance/add/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection/add" />} />
      <RouterRoute path={'/:regionCode/insurance/details/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection/details" />} />
      <RouterRoute path={'/:regionCode/insurance/payment/*'} element={<RedirectWithStatus code={301} to="/:regionCode/travel-protection/payment" />} />
      {/* Gift card pages */}
      <RouterRoute path={'/:regionCode/giftcards/*'} element={<RedirectWithStatus code={301} to="/:regionCode/gift-cards" />} />
      <RouterRoute path={'/:regionCode/giftcard/*'} element={<RedirectWithStatus code={301} to="/:regionCode/gift-cards" />} />
      <RouterRoute path="/:regionCode/gift-card" element={<RedirectWithStatus code={301} to="/:regionCode/gift-cards" />} />
      <RouterRoute path={'/:regionCode/gift-cards/confirmation/*'} element={<AuthenticatedRouteElement path="/:regionCode/gift-cards/confirmation"><GiftCardConfirmationPage /></AuthenticatedRouteElement>} />
      {/* Purchase credits page */}
      {isSpoofed && <RouterRoute path={'/:regionCode/purchase-credits/*'} element={<TrackedRouteElement element={<PurchaseCreditsPage />} path="/:regionCode/purchase-credits" />} />}
      <RouterRoute path={'/:regionCode/gift-card/:code/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/gift-card/:code"><GiftCardViewPage /></AuthenticatedRouteElement>} />
      {/* Booking pages */}
      <RouterRoute path={'/:regionCode/booking/guest-confirmation/*'} element={<TrackedRouteElement element={<GuestConfirmationPage />} path="/:regionCode/booking/guest-confirmation" />} />
      <RouterRoute path={'/:regionCode/booking/confirmation/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/booking/confirmation"><ConfirmationPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/bb/booking/confirmation/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/bb/booking/confirmation"><ConfirmationPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/confirmation/:orderId/*'} element={<RedirectWithStatus code={301} to="/:regionCode/booking/confirmation?orderId=:orderId" />} />
      <RouterRoute path={'/:regionCode/bb/booking/confirmation/*'} element={<RedirectWithStatus code={301} to="/:regionCode/booking/confirmation" />} />
      <RouterRoute path={'/:regionCode/bb/booking/:step/*'} element={<RedirectWithStatus code={301} to="/:regionCode/booking/:step" />} />
      {/* Redeem gift hotel and experience */}
      <RouterRoute path="/:regionCode/redeem/:redeemCode/:orderId/:offerId" element={<TrackedRouteElement element={<RedeemGifted />} path="/:regionCode/redeem/:redeemCode/:orderId/:offerId" />} />
      {/* TourV2Pages */}
      <RouterRoute path="/:regionCode/tour/:offerId" element={<TrackedRouteElement element={<TourV2OfferPageElement />} path="/:regionCode/tour/:offerId" />} />
      <RouterRoute path="/:regionCode/tour/:offerId/:variationId" element={<TrackedRouteElement element={<TourV2OfferVariationPage />} path="/:regionCode/tour/:offerId/:variationId" />} />
      <RouterRoute path="/:regionCode/search/tours" element={<TrackedRouteElement element={<TourV2SearchPage />} path="/:regionCode/search/tours" />} />
      <RouterRoute path="/:regionCode/tours/deals/:slug" element={<TrackedRouteElement element={<TourDealPage />} path="/:regionCode/tours/deals/:slug" />} />
      {/* Tour signature series contentful redirects */}
      <RouterRoute path={'/:regionCode/signature-series.p1/*'} element={<RedirectWithStatus code={301} to="/:regionCode/campaign/signature-series" />} />
      <RouterRoute path={'/:regionCode/indigo/*'} element={<RedirectWithStatus code={301} to="/:regionCode/offer/hotel-indigo-melbourne-little-collins-victoria-australia/006RF00000MZ9gTYAT" />} />
      {/* Tailor-Made Tours */}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && <RouterRoute
        path="/:regionCode/tailor-made-tours/requests/:requestId"
        element={<TrackedRouteElement
          element={
            <TailorMadeTourRequestConfirmationPage />
          }
          path="/:regionCode/tailor-made-tours/requests/:requestId"
        />}
      />}
      {config.TAILOR_MADE_TOURS_ENABLED && config.TAILOR_MADE_TOURS_REGIONS && isEnabledForFeature(config.TAILOR_MADE_TOURS_REGIONS, currentRegionCode) && <RouterRoute
        path="/:regionCode/tailor-made-tours/requests"
        element={<TrackedRouteElement
          element={
            <TailorMadeTourRequestPage />
          }
          path="/:regionCode/tailor-made-tours/requests"
        />}
      />}
      <RouterRoute path="/:regionCode/tours/tours-catalogue" element={<TrackedRouteElement element={<TourCataloguePage />} path="/:regionCode/tours/tours-catalogue" />} />
      {config.hasReferralProgram && <RouterRoute
        path="/:regionCode/refer"
        element={<TrackedRouteElement
          element={
            <ReferPage />
          }
          path="/:regionCode/refer"
        />}
      />}
      {config.hasReferralProgram && <RouterRoute
        path={'/:regionCode/refer/:referralCode/*'}
        element={<TrackedRouteElement
          element={
            <RefereeLandingPage />
          }
          path="/:regionCode/refer/:referralCode"
        />}
      />}
      <RouterRoute path={'/:regionCode/google-hotel-ads/*'} element={<TrackedRouteElement element={<GoogleHotelAdPage />} path="/:regionCode/google-hotel-ads" />} />
      <RouterRoute path={'/:regionCode/property/ayodya-resort-bali-bedbank:6486e2f1-958e-4f91-a5b0-481205904898/*'} element={<RedirectWithStatus code={302} to="/:regionCode/offer/ayodya-resort-bali-nusa-dua-indonesia-asia/0062y00000JN0RvAAL" />} />
      <RouterRoute path={'/:regionCode/abercrombie-and-kent-tours.p1/*'} element={<RedirectWithStatus code={302} to="/:regionCode/tours" />} />
      <RouterRoute path={'/:regionCode/property/:propertyNameAndId/*'} element={<TrackedRouteElement element={<PropertyPage />} path="/:regionCode/property/:propertyNameAndId" />} />
      {config.ENABLE_AU_COMPETITION && <RouterRoute path={'/:regionCode/hunter-valley-competition/*'} element={<TrackedRouteElement element={<AuCompetitionsPage />} path="/:regionCode/hunter-valley-competition" />} />}
      {config.ENABLE_INTL_COMPETITION && <RouterRoute path={'/:regionCode/spin-the-wheel-competition/*'} element={<TrackedRouteElement element={<IntlCompetitionsPage />} path="/:regionCode/spin-the-wheel-competition" />} />}
      <RouterRoute path={'/:regionCode/NHCollectionDubaiThePalm/*'} element={<TrackedRouteElement element={pageElement(ukCompetitionsPage)} path="/:regionCode/NHCollectionDubaiThePalm" />} />
      <RouterRoute path={'/:regionCode/citi/*'} element={<RedirectWithStatus code={301} to="/:regionCode/save-with-citi.p1" />} />
      <RouterRoute path={'/:regionCode/terms/*'} element={<RedirectWithStatus code={301} to="/:regionCode/terms-and-conditions" />} />
      <RouterRoute
        path={'/:regionCode/sharethis/verify/*'}
        element={<TrackedRouteElement
          element={

            <ShareThisVerifyRouteElement />
          }
          path="/:regionCode/sharethis/verify"
        />}
      />

      {/* Content hub pages */}
      {config.CONTENT_HUB_ENABLED && config.contentHub && <RouterRoute
        path={'/:regionCode/pages/:slug/*'}
        element={
          <RedirectWithStatus code={301} to="/:regionCode/:slug.p1" />
        }
      />}
      {config.CONTENT_HUB_ENABLED && config.contentHub && <RouterRoute
        path={`/:regionCode/:slug.${REGIONLESS_DYNAMIC_ROUTE_SUFFIXES.contentHub}`}
        element={<TrackedRouteElement
          element={
            <ContentHub />
          }
          path={`/:regionCode/:slug.${REGIONLESS_DYNAMIC_ROUTE_SUFFIXES.contentHub}`}
        />}
      />}
      {/* Support has a first-class route above; keep the static page config for navigation links only. */}
      {config.staticPages.filter(sp => sp.type !== 'support').map((sp) =>
        sp.url?.to && <RouterRoute

          key={sp.url.to}
          path={`/:regionCode${sp.url.to}`}
          element={<TrackedRouteElement
            element={
              <StaticPageRouteElement
                staticPage={sp}
                title={typeof sp.text === 'string' ? sp.text : intl.formatMessage(sp.text)}
              />
            }
            path={`/:regionCode${sp.url.to}`}
          />}
        />,

      )}
      {/* Promotional Pages */}
      {!standaloneFlightsEnabled && <RouterRoute path={'/:regionCode/flights/*'} element={<TrackedRouteElement element={<FlightsLandingPage />} path="/:regionCode/flights" />} />}
      {standaloneFlightsEnabled && <RouterRoute path="/:regionCode/flights/:dealCategory?" element={<TrackedRouteElement element={<FlightsSearchLandingPage />} path="/:regionCode/flights/:dealCategory?" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={'/:regionCode/flights-search/*'} element={<TrackedRouteElement element={<FlightsSearchPage />} path="/:regionCode/flights-search" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={'/:regionCode/flights-search-results/*'} element={<TrackedRouteElement element={<FlightsSearchResultsPage />} path="/:regionCode/flights-search-results" />} />}
      {standaloneFlightsEnabled && <RouterRoute path={'/:regionCode/flights-search-results-multi-city/*'} element={<TrackedRouteElement element={pageElement(FlightsSearchMultiCityResultsPage)} path="/:regionCode/flights-search-results-multi-city" />} />}
      <RouterRoute path={'/:regionCode/flight/deal/:id/*'} element={<TrackedRouteElement element={<FlightDealLandingPage />} path="/:regionCode/flight/deal/:id" />} />
      <RouterRoute path={'/:regionCode/flights/campaigns/:id/*'} element={<TrackedRouteElement element={<FlightCampaignsLandingPage />} path="/:regionCode/flights/campaigns/:id" />} />
      <RouterRoute path={'/:regionCode/escapes-finder/*'} element={<TrackedRouteElement element={<EscapesFinderPage />} path="/:regionCode/escapes-finder" />} />
      <RouterRoute path={'/:regionCode/disneyland-offers/*'} element={<RedirectWithStatus code={301} to="/:regionCode/experience/led%3A0062y00000Gui6KAAR" />} />
      <RouterRoute path={'/:regionCode/disneyland-offers-test/*'} element={<RedirectWithStatus code={301} to="/:regionCode/experience/led%3A0062y000009EEOEAA4" />} />
      <RouterRoute path={'/:regionCode/spotify/*'} element={<RedirectWithStatus to="/:regionCode/campaign/spotify" />} />
      <RouterRoute path={'/:regionCode/nyc/*'} element={<RedirectWithStatus to="/:regionCode/campaign/new-york" />} />
      <RouterRoute path={'/:regionCode/traveltester/*'} element={<RedirectWithStatus to="/:regionCode/campaign/7news-traveltester" />} />
      <RouterRoute
        path="/:regionCode/friends/*"
        element={<LocaleAwareTrackedRoutes routes={friendsReferralRoutes} />}
      />

      {config.promotionalPages.map((pp) => {
        const PromotionalComponent = promotionalPages[pp.component]
        return <RouterRoute
          key={pp.url}
          path={`/:regionCode${pp.url}/*`}
          element={<TrackedRouteElement
            element={
              <PromotionalComponent />
            }
            path={`/:regionCode${pp.url}`}
          />}
        />
      })}
      {config.holidayPages && <RouterRoute
        path={'/:regionCode/holidays/:slug/*'}
        element={
          <RedirectWithStatus code={301} to="/:regionCode/:slug.h1" />
        }
      />}
      {config.holidayPages && <RouterRoute
        path={`/:regionCode/:slug.${REGIONLESS_DYNAMIC_ROUTE_SUFFIXES.holiday}/*`}
        element={<TrackedRouteElement
          element={
            <HolidayLandingPageElement />
          }
          path={`/:regionCode/:slug.${REGIONLESS_DYNAMIC_ROUTE_SUFFIXES.holiday}`}
        />}
      />}
      {config.destinationPages && <RouterRoute
        path={`/:regionCode/:slug.${REGIONLESS_DYNAMIC_ROUTE_SUFFIXES.destination}/*`}
        element={
          <RedirectWithStatus code={301} to="/:regionCode/destination/:slug" />
        }
      />}
      <RouterRoute path={'/:regionCode/campaign/lux-list-travel-travel-awards-voting/*'} element={<RedirectWithStatus code={301} to="/:regionCode/campaign/lux-list-travel-awards" />} />
      <RouterRoute path={'/:regionCode/campaign/lux-list-travel-travel-awards-voting-uk/*'} element={<RedirectWithStatus code={301} to="/:regionCode/campaign/lux-list-travel-awards-uk" />} />
      <RouterRoute path={'/:regionCode/campaign/lux-list-travel-travel-awards-voting-us/*'} element={<RedirectWithStatus code={301} to="/:regionCode/campaign/lux-list-travel-awards-us" />} />
      {/* Redirect due to wrong URL sent out */}
      <RouterRoute path={'/:regionCode/campaign/singapore/competition-april/*'} element={<RedirectWithStatus code={301} to="/:regionCode/campaign/competition-april" />} />
      <RouterRoute path="/:regionCode/campaign/lux-list-travel-awards" element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards" />} />
      <RouterRoute path="/:regionCode/campaign/lux-list-travel-awards-prizes" element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-prizes" />} />
      <RouterRoute path="/:regionCode/campaign/lux-list-travel-awards-us" element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-us" />} />
      <RouterRoute path="/:regionCode/campaign/lux-list-travel-awards-prizes-us" element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-prizes-us" />} />
      <RouterRoute path="/:regionCode/campaign/lux-list-travel-awards-uk" element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-uk" />} />
      <RouterRoute path="/:regionCode/campaign/lux-list-travel-awards-prizes-uk" element={<TrackedRouteElement element={<LuxAwardPage />} path="/:regionCode/campaign/lux-list-travel-awards-prizes-uk" />} />
      <RouterRoute
        path={`/:regionCode/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`}
        element={<AuthenticatedRouteElement showPopup path={`/:regionCode/campaign/${FRIENDS_AND_FAMILY_DISCOUNT_PAGE_SLUG}`}><FriendsAndFamilyInviteRouteElement
          isEnabled={isFriendsAndFamilyPageEnabled}
          isStaff={isStaff}
        />
        </AuthenticatedRouteElement>}
      />

      <RouterRoute path={`/:regionCode/campaign/${CHARTER_PAGE_SLUG}`} element={<TrackedRouteElement element={<MaldivesDirectSearchPage />} path={`/:regionCode/campaign/${CHARTER_PAGE_SLUG}`} />} />
      <RouterRoute path={`/:regionCode/campaign/${CHARTER_BEFORE_YOU_FLY_PAGE_SLUG}`} element={<TrackedRouteElement element={<MaldivesDirectBeforeYouFlyPage />} path={`/:regionCode/campaign/${CHARTER_BEFORE_YOU_FLY_PAGE_SLUG}`} />} />
      <RouterRoute path={'/:regionCode/destination/*'} element={<TrackedRouteElement element={<ContentfulWebPage />} path={'/:regionCode/destination/*'} />} />
      <RouterRoute path={'/:regionCode/campaign/*'} element={<TrackedRouteElement element={<ContentfulWebPage />} path={'/:regionCode/campaign/*'} />} />
      <RouterRoute path={'/:regionCode/hotels/country/maldives/*'} element={<RedirectWithStatus code={301} to="/:regionCode/destination/maldives" />} />
      <RouterRoute path={'/:regionCode/hotels/country/bali/*'} element={<RedirectWithStatus code={301} to="/:regionCode/destination/bali" />} />
      <RouterRoute path={'/:regionCode/hotels/country/greece/*'} element={<RedirectWithStatus code={301} to="/:regionCode/destination/greece" />} />
      <RouterRoute path={'/:regionCode/hotels/country/spain/*'} element={<RedirectWithStatus code={301} to="/:regionCode/destination/spain" />} />
      {/* New URL Structure For SEO Destination/Product pages */}
      <RouterRoute path="/:regionCode/:productType/country/:country" element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/country/:country" />} />
      <RouterRoute path="/:regionCode/:productType/city/:city/:state/:country" element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/city/:city/:state/:country" />} />
      <RouterRoute path="/:regionCode/:productType/city/:city/:country" element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/city/:city/:country" />} />
      <RouterRoute path="/:regionCode/:productType/location/:location/:city/:state/:country" element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/location/:location/:city/:state/:country" />} />
      <RouterRoute path="/:regionCode/:productType/location/:location/:city/:country" element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/location/:location/:city/:country" />} />
      <RouterRoute path="/:regionCode/:productType/location/:location/:country" element={<TrackedRouteElement element={pageElement(DestinationProductPage)} path="/:regionCode/:productType/location/:location/:country" />} />
      {/* HolidayPackage Pages: Rule of URL: [base]/holiday-packages/:location/:holidayType */}
      <RouterRoute path="/:regionCode/holiday-packages/:location/:holidayType" element={<TrackedRouteElement element={pageElement(DestinationLandingPage)} path="/:regionCode/holiday-packages/:location/:holidayType" />} />
      <RouterRoute path="/:regionCode/holiday-packages/:location" element={<TrackedRouteElement element={pageElement(DestinationLandingPage)} path="/:regionCode/holiday-packages/:location" />} />
      <RouterRoute path="/:regionCode/resorts/:location" element={<TrackedRouteElement element={pageElement(DestinationLandingPage)} path="/:regionCode/resorts/:location" />} />
      {/* SEO Destination/Product pages */}
      <RouterRoute path={DESTINATION_PRODUCT_DOT_ROUTE_PATH} element={<TrackedRouteElement element={<DestinationProductDotRouteElement />} path={DESTINATION_PRODUCT_DOT_ROUTE_PATH} />} />
      {config.LERE_ENABLE_TOP_PICKS_FOR_ME && <RouterRoute path={'/:regionCode/top-picks-for-me/*'} element={<TrackedRouteElement element={<TopPicksForMePage />} path="/:regionCode/top-picks-for-me" />} />}
      <RouterRoute
        path={'/:regionCode/ancillary-payment/:orderId/:type/*'}
        element={<AuthenticatedRouteElement
          showPopup
          path="/:regionCode/ancillary-payment/:orderId/:type"
        >{

            pageElement(AncillaryPaymentPage)
          }
        </AuthenticatedRouteElement>}
      />

      <RouterRoute
        path={'/:regionCode/ancillary-change-reservation/:orderId/:type/:itemId?/*'}
        element={<AuthenticatedRouteElement
          showPopup
          path="/:regionCode/ancillary-change-reservation/:orderId/:type/:itemId?"
        >{

            pageElement(AncillaryChangeReservationPage)
          }
        </AuthenticatedRouteElement>}
      />
      <RouterRoute path={'/:regionCode/checkout/bnbl/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/bnbl/order/:orderId/item/:itemId"><RedirectCheckoutBNBL /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/change-dates/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/change-dates/order/:orderId/item/:itemId"><RedirectCheckoutChangeDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/confirm-dates/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/confirm-dates/order/:orderId/item/:itemId"><RedirectCheckoutConfirmDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/extend-dates/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/extend-dates/order/:orderId/item/:itemId"><RedirectCheckoutExtendDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/add-extra-guests/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/add-extra-guests/order/:orderId/item/:itemId"><RedirectCheckoutAddExtraGuests /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/upgrade-room/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/upgrade-room/order/:orderId/item/:itemId"><RedirectCheckoutRoomUpgrade /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/partner-room-upgrade/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/partner-room-upgrade/order/:orderId/item/:itemId"><RedirectCheckoutPartnerRoomUpgrade /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/flight-change-dates/order/:orderId/item/:itemId/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout/flight-change-dates/order/:orderId/item/:itemId"><RedirectCheckoutFlightChangeDates /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout-restore-cart/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout-restore-cart"><CheckoutRestoreCartPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout-gateway/*'} element={<AuthenticatedRouteElement showPopup path="/:regionCode/checkout-gateway"><CheckoutGatewayPage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/checkout/*'} element={<TrackedRouteElement element={<CheckoutPageRoutes />} path="/:regionCode/checkout/*" />} />
      {isTripPlannerRoute && <RouterRoute
        path="/:regionCode/trip-planner/*"
        element={
          <TripPlannerRoutes />
        }
      />}
      <RouterRoute path="/:regionCode/cart" element={<AuthenticatedRouteElement showPopup path="/:regionCode/cart"><UserCartPage /></AuthenticatedRouteElement>} />
      <RouterRoute path="/:regionCode/cart/s/:sharedCartId" element={<AuthenticatedRouteElement accountAccessModalInitialMode="sharedCartInvite" showPopup path="/:regionCode/cart/s/:sharedCartId"><SharedCartInvitePage /></AuthenticatedRouteElement>} />
      <RouterRoute path={'/:regionCode/user-cart/*'} element={<RedirectWithStatus code={301} to="/:regionCode/cart" />} />
      <RouterRoute path="/:regionCode/hotels/:holidayType" element={<TrackedRouteElement element={<HolidayTypeLandingPage />} path="/:regionCode/hotels/:holidayType" />} />
      <RouterRoute path="/:regionCode/hotels/:holidayType/:country" element={<TrackedRouteElement element={<HolidayTypeLandingPage />} path="/:regionCode/hotels/:holidayType/:country" />} />
      <RouterRoute path="/:regionCode/hotels/:holidayType/:country/:cityOrState" element={<TrackedRouteElement element={<HolidayTypeLandingPage />} path="/:regionCode/hotels/:holidayType/:country/:cityOrState" />} />
      <RouterRoute
        path="/:regionCode/orders/:orderId/accept-invite/:invitationId"
        element={<TrackedRouteElement
          element={

            <AcceptOrderInvitePage />
          }
          path="/:regionCode/orders/:orderId/accept-invite/:invitationId"
        />}
      />

      {config.SECURE_PAYMENT_ENABLED && <RouterRoute
        path="/:regionCode/pay/:paymentLinkId"
        element={<TrackedRouteElement
          element={
            <SecurePaymentPage />
          }
          path="/:regionCode/pay/:paymentLinkId"
        />}
      />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount && <RouterRoute
        path={`/:regionCode${LE_AGENT_HUB_SOCIETE_CONFIRMATION}`}
        element={<TrackedRouteElement element={<SocieteConfirmationPage />} path={`/:regionCode${LE_AGENT_HUB_SOCIETE_CONFIRMATION}`} />}
      />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount && <RouterRoute
        path={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`}
        element={<TrackedRouteElement element={<AgentHubAccountSetupCompleted />} path={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`} />}
      />}
      {config.agentHub.isEnabled && hasSetupAgentHubAccount && <RouterRoute
        path={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/complete-account-setup/*`}
        element={<RedirectWithStatus
          to={`/:regionCode/${agentHubOrSubBrandConfig.basePath}/account-setup-completed`}
        />}
      />}
      {config.SPOOFING_SEND_CONFIRMATION_EMAIL && <RouterRoute path={'/:regionCode/skycheck/*'} element={<TrackedRouteElement element={<SkyCheckConfirmationPage />} path="/:regionCode/skycheck" />} />}
      {isShopEnabled && isShopLocale && isMerchandiseRoute &&
        <>
          <RouterRoute
            path="/:regionCode/shop/*"
            element={
              <>
                <LocaleAwareTrackedRoutes routes={merchandiseShopNavRoutes} />
                <LocaleAwareTrackedRoutes routes={merchandiseShopPageRoutes} />
              </>
            }
          />
          <RouterRoute
            path="/:regionCode/search/shop/*"
            element={
              <>
                <LocaleAwareTrackedRoutes routes={merchandiseSearchNavRoutes} />
                <LocaleAwareTrackedRoutes routes={merchandiseSearchPageRoutes} />
              </>
            }
          />
        </>}
      {/* Mobile app marketing url, redirect this to home page until we have one */}
      <RouterRoute path={'/:regionCode/app/*'} element={<RedirectWithStatus code={302} to="/:regionCode/" />} />
      <RouterRoute
        path="*"
        element={<TrackedRouteElement
          element={
            <HttpStatus code={404}>
              <Error404PageWithOffers />
            </HttpStatus>
          }
          path="*"
        />}
      />

    </>)
}

export function createPageRouteObjects(
  options: PageRouteOptions,
): Array<RouteObject> {
  return createRoutesFromElements(createPageRouteElements(options))
}

const ssrNotFoundCheckRouteObjectsByRegion = new Map<string, Array<RouteObject>>()

export function createSsrNotFoundCheckRouteObjects(
  options: PageRouteOptions,
): Array<RouteObject> {
  const cached = ssrNotFoundCheckRouteObjectsByRegion.get(options.currentRegionCode)
  if (cached) return cached

  const matchingOptions = {
    ...options,
    ...OPTIONAL_ROUTE_FLAGS_ENABLED,
    registerPathnameGatedRoutes: true,
  }
  const routeObjects = [...createPageRouteObjects(matchingOptions)]

  // One tree per flag flipped off so `!flag && <Route>` cannot go stale.
  for (const flag of OPTIONAL_ROUTE_FLAG_KEYS) {
    routeObjects.push(...createPageRouteObjects({
      ...matchingOptions,
      [flag]: false,
    }))
  }

  ssrNotFoundCheckRouteObjectsByRegion.set(options.currentRegionCode, routeObjects)
  return routeObjects
}

export function isPageRouteNotFound(
  routeObjects: Array<RouteObject> | undefined,
  pathname: string,
): boolean {
  if (!routeObjects) return false

  if (matchLocaleAwarePath(pathname, '/:regionCode/splashpage')) {
    return false
  }

  const localeStrippedPathname = new UrlLocation(pathname).withoutLocale().path
  const matchedRoutePath = matchRoutes(
    routeObjects,
    localeStrippedPathname,
  )?.at(-1)?.route.path

  if (matchedRoutePath === '*') return true

  if (
    matchedRoutePath === DESTINATION_PRODUCT_DOT_ROUTE_PATH &&
    !matchLocaleAwarePath(pathname, '/:regionCode/splashpage')
  ) {
    return !matchDestinationProductDotRoute(localeStrippedPathname)
  }

  return false
}

function getFeatureFlag(
  state: App.State,
  flagId: OptimizelyFeatureFlags,
): boolean {
  return isAllowedToTriggerExperiment(true, state.utm.content, flagId) &&
    getOptimizelyFeatureFlagVariation(state, flagId)
}

function getPageRouteOptions(
  state: App.State,
  intl: IntlShape,
  pathname: string,
): PageRouteOptions {
  const agentHubAffiliationRules = getAgentHubAffiliationRules(state)
  const hasSetupAgentHubAccount =
    state.agentHub.agentHubAccount.account?.status === 'onboarded'

  return {
    ...getPageRoutesProps(state),
    canBookCarHire: !isAgentHubEnabled || agentHubAffiliationRules.canBookCarHire,
    canBookCruise: !isAgentHubEnabled || agentHubAffiliationRules.canBookCruise,
    hasSetupAgentHubAccount,
    intl,
    isFriendsAndFamilyPageEnabled: getFeatureFlag(
      state,
      OptimizelyFeatureFlags.maldivesCharterFnfPage,
    ),
    isLoyaltyLuxplusLandingPageEnabled: getFeatureFlag(
      state,
      OptimizelyFeatureFlags.loyaltyLuxplusLandingPage,
    ),
    isLoyaltyPurchasePointsEnabled: getIsLoyaltyPurchasePointsEnabled(state),
    isMaldivesCharterEnabled: isMaldivesCharterCarouselEnabled(state),
    isShopEnabled: getIsMerchandiseEnabled(state),
    isShopLocale: isEnglishLocale(state.geo.locale),
    isSpoofed: state.auth.account.isSpoofed,
    isStaff: isLEStaff(state),
    pathname,
  }
}

export function getPageRouteObjects(
  state: App.State,
  intl: IntlShape,
  pathname: string,
): Array<RouteObject> | undefined {
  const routeOptions = getPageRouteOptions(state, intl, pathname)

  if (config.agentHub.isEnabled && !routeOptions.hasSetupAgentHubAccount) {
    return undefined
  }

  return createSsrNotFoundCheckRouteObjects(routeOptions)
}

export function PageRoutes(props: Props) {
  const intl = useIntl()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => ({
    ...location,
    pathname: new UrlLocation(location.pathname).withoutLocale().path,
  }), [location])
  const hasSetupAgentHubAccount = useAppSelector(
    state => state.agentHub.agentHubAccount.account?.status === 'onboarded',
  )
  const agentHubAffiliationRules = useAppSelector(getAgentHubAffiliationRules)
  const isShopEnabled = useAppSelector(getIsMerchandiseEnabled)
  const locale = useAppSelector(state => state.geo.locale)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const isStaff = useAppSelector(isLEStaff)
  const isFriendsAndFamilyPageEnabled = useFeatureFlag(
    OptimizelyFeatureFlags.maldivesCharterFnfPage,
  )
  const isLoyaltyLuxplusLandingPageEnabled = useFeatureFlag(
    OptimizelyFeatureFlags.loyaltyLuxplusLandingPage,
  )
  const isLoyaltyPurchasePointsEnabled = useAppSelector(
    getIsLoyaltyPurchasePointsEnabled,
  )
  const isMaldivesCharterEnabled = useAppSelector(
    isMaldivesCharterCarouselEnabled,
  )
  const routeOptions = useMemo<PageRouteOptions>(() => ({
    ...props,
    canBookCarHire:
      !isAgentHubEnabled || agentHubAffiliationRules.canBookCarHire,
    canBookCruise: !isAgentHubEnabled || agentHubAffiliationRules.canBookCruise,
    hasSetupAgentHubAccount,
    intl,
    isFriendsAndFamilyPageEnabled,
    isLoyaltyLuxplusLandingPageEnabled,
    isLoyaltyPurchasePointsEnabled,
    isMaldivesCharterEnabled,
    isShopEnabled,
    isShopLocale: isEnglishLocale(locale),
    isSpoofed,
    isStaff,
    pathname: location.pathname,
  }), [
    agentHubAffiliationRules.canBookCarHire,
    agentHubAffiliationRules.canBookCruise,
    hasSetupAgentHubAccount,
    intl,
    isFriendsAndFamilyPageEnabled,
    isLoyaltyLuxplusLandingPageEnabled,
    isLoyaltyPurchasePointsEnabled,
    isMaldivesCharterEnabled,
    isShopEnabled,
    isSpoofed,
    isStaff,
    locale,
    location.pathname,
    props,
  ])
  const routeObjects = useMemo(
    () => createPageRouteObjects(routeOptions),
    [routeOptions],
  )
  const pageRoutes = useRoutes(routeObjects, localeStrippedLocation)

  if (config.agentHub.isEnabled && !hasSetupAgentHubAccount) {
    return <AgentHubGuestPageRoutes />
  }

  return pageRoutes
}

export function getPageRoutesProps(state: App.State): Props {
  return {
    currentRegionCode: state.geo.currentRegionCode,
    isLuxPlusEnabled: getIsLuxPlusEnabled(state),
    isLoyaltyEnabled: getIsLoyaltyEnabled(state),
    standaloneFlightsEnabled: isStandaloneFlightsEnabled(state) || isFlightsCredit(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
}

export default connect(getPageRoutesProps)(PageRoutes)
